/**
 * Paneel voor deterministische diagram-vereenvoudiging (reductie-transforms).
 *
 * Niet-destructief via een KOPIE: de gekozen transforms produceren een nieuw
 * diagram "<naam> (vereenvoudigd)" waarin de betreffende concepten verborgen
 * of ingeklapt zijn; het origineel blijft ongewijzigd. Zo houd je het detail-
 * diagram én een management-versie naast elkaar als aparte tabbladen.
 *
 * Fase 1: hide-leaves, critical-path (2 geselecteerde eindpunten) en
 * cluster-by-composition. Fase 2: layer-collapse — vouw hele lagen samen tot
 * één synthetische super-node (`type: "supernode"`-viewState-entry, géén echt
 * concept); de members' relaties herankeren op het blok. Plus promote-to-
 * abstraction — auto-gedetecteerde realisatie-clusters (ApplicationService +
 * realiserende Components/Functions) óf de huidige canvas-selectie vouwen tot
 * één ApplicationCollaboration-gestileerd blok.
 */
import { detectRealizationClusters, type SimplifyRelationRef } from "@ae/domain";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { type PromoteCluster, useWorkspaceData } from "../../state/workspace";

export function SimplifyPanel({
  workspaceId,
  branchId,
}: {
  workspaceId: string | null;
  branchId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("nl") ? "nl" : "en";
  const open = useUi((s) => s.simplifyPanelOpen);
  const setOpen = useUi((s) => s.setSimplifyPanelOpen);
  const selectedConceptIds = useUi((s) => s.selectedConceptIds);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const createSimplifiedCopy = useWorkspaceData((s) => s.createSimplifiedCopy);
  const sessionWs = useSession((s) => s.workspaceId);
  // Alleen kijken: vereenvoudigen maakt een nieuw diagram — dat is schrijven.
  const readOnly = useSession((s) => s.readOnly);
  const wsId = workspaceId ?? sessionWs;

  const revision = useWorkspaceData((s) => s.revision);
  const [hideLeaves, setHideLeaves] = useState(false);
  const [cluster, setCluster] = useState(false);
  const [useCriticalPath, setUseCriticalPath] = useState(false);
  const [collapseLayers, setCollapseLayers] = useState<Set<string>>(new Set());
  const [promoteAnchors, setPromoteAnchors] = useState<Set<string>>(new Set());
  const [promoteSelection, setPromoteSelection] = useState(false);
  const [autoLayout, setAutoLayout] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "error" | "info"; text: string } | null>(null);

  // Lagen die daadwerkelijk op het actieve diagram voorkomen — dat zijn de
  // kandidaten voor layer-collapse (Fase 2, synthetische super-node).
  const layersOnDiagram = useMemo(() => {
    void revision;
    if (!open) return [];
    const store = useWorkspaceData.getState().store;
    const diagram = activeDiagramId ? store.getDiagram(activeDiagramId) : null;
    if (!diagram) return [];
    const reg = getRegistry();
    const layers = new Set<string>();
    for (const [k, ve] of Object.entries(diagram.viewState)) {
      if (ve.type !== "object" || !ve.position || ve.hidden) continue;
      const concept = store.getConcept(ve.ref ?? k);
      const layer = concept ? reg.getElementType(concept.typeId)?.layer : undefined;
      // Tekenen-primitieven zijn niet-semantisch — geen collapse-kandidaat.
      if (layer && layer !== "drawing") layers.add(layer);
    }
    return [...layers].sort();
  }, [open, activeDiagramId, revision]);

  // Auto-gedetecteerde realisatie-clusters op het actieve diagram
  // (ApplicationService + realiserende Components/Functions). Subtype-bewust:
  // typeIds worden naar hun base-pack-type gecollapst zodat een subtype-van-
  // ApplicationComponent ook meetelt. Label = de service-naam (het anker).
  const promoteClusters = useMemo(() => {
    void revision;
    if (!open) return [];
    const store = useWorkspaceData.getState().store;
    const diagram = activeDiagramId ? store.getDiagram(activeDiagramId) : null;
    if (!diagram) return [];
    const reg = getRegistry();
    const present = new Set<string>();
    for (const [k, ve] of Object.entries(diagram.viewState)) {
      if (ve.type !== "object" || !ve.position || ve.hidden) continue;
      present.add(ve.ref ?? k);
    }
    const conceptTypeIds: Record<string, string> = {};
    for (const cid of present) {
      const concept = store.getConcept(cid);
      if (concept) conceptTypeIds[cid] = reg.baseTypeIdFor(concept.typeId);
    }
    const relations: SimplifyRelationRef[] = [];
    for (const [k, ve] of Object.entries(diagram.viewState)) {
      if (ve.type !== "relation") continue;
      const rel = store.getRelation(ve.ref ?? k);
      if (rel && present.has(rel.sourceConceptId) && present.has(rel.targetConceptId)) {
        relations.push({
          id: rel.id,
          typeId: rel.typeId,
          sourceConceptId: rel.sourceConceptId,
          targetConceptId: rel.targetConceptId,
        });
      }
    }
    const clusters = detectRealizationClusters(conceptTypeIds, relations);
    return clusters.map((c) => {
      const anchor = store.getConcept(c.anchorId);
      const name = anchor?.name?.[lang] || anchor?.name?.en || anchor?.name?.nl || "";
      return { anchorId: c.anchorId, memberIds: c.memberIds, name };
    });
  }, [open, activeDiagramId, revision, lang]);

  if (!open || readOnly) return null;

  const canCriticalPath = selectedConceptIds.length === 2;
  const critical = useCriticalPath && canCriticalPath;
  const activeCollapseLayers = [...collapseLayers].filter((l) => layersOnDiagram.includes(l));
  const canPromoteSelection = selectedConceptIds.length >= 2;

  // Bouw de te promoveren clusters uit de gekozen auto-clusters + eventueel de
  // huidige canvas-selectie.
  const activePromoteClusters: PromoteCluster[] = [];
  for (const c of promoteClusters) {
    if (promoteAnchors.has(c.anchorId)) {
      activePromoteClusters.push({
        memberConceptIds: c.memberIds,
        ...(c.name ? { label: { [lang]: c.name } } : {}),
      });
    }
  }
  if (promoteSelection && canPromoteSelection) {
    activePromoteClusters.push({ memberConceptIds: [...selectedConceptIds] });
  }

  const anyTransform =
    hideLeaves ||
    cluster ||
    critical ||
    activeCollapseLayers.length > 0 ||
    activePromoteClusters.length > 0;

  const toggleLayer = (layer: string) => {
    setCollapseLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layer)) next.delete(layer);
      else next.add(layer);
      return next;
    });
  };

  const togglePromote = (anchorId: string) => {
    setPromoteAnchors((prev) => {
      const next = new Set(prev);
      if (next.has(anchorId)) next.delete(anchorId);
      else next.add(anchorId);
      return next;
    });
  };

  const generate = async () => {
    if (busy || !anyTransform || !wsId || !branchId || !activeDiagramId) return;
    if (useSession.getState().readOnly) return;
    setBusy(true);
    setMsg(null);
    try {
      const options = {
        hideLeaves,
        clusterByComposition: cluster,
        criticalPath:
          critical && selectedConceptIds[0] && selectedConceptIds[1]
            ? { sourceId: selectedConceptIds[0], targetId: selectedConceptIds[1] }
            : null,
        collapseLayers: activeCollapseLayers,
        promoteClusters: activePromoteClusters,
      };
      const res = await createSimplifiedCopy(wsId, branchId, activeDiagramId, options, autoLayout);
      if (!res) {
        setMsg({ kind: "error", text: t("simplify.failed") });
        return;
      }
      setMsg({ kind: "info", text: t("simplify.done", { count: res.hiddenCount }) });
    } catch {
      setMsg({ kind: "error", text: t("simplify.failed") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ledger absolute right-2 top-14 z-40 flex w-80 flex-col overflow-hidden rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl">
      <div className="flex items-center justify-between border-(--ld-border) border-b px-3 py-2">
        <div className="font-semibold text-(--ld-text) text-sm">{t("simplify.title")}</div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded px-1.5 text-(--ld-muted) hover:bg-(--ld-raised)"
          title={t("simplify.close")}
        >
          ✕
        </button>
      </div>

      {!activeDiagramId ? (
        <div className="p-4 text-(--ld-muted) text-sm">{t("simplify.noDiagram")}</div>
      ) : (
        <div className="flex flex-col gap-3 p-3">
          <p className="text-(--ld-muted) text-xs">{t("simplify.intro")}</p>

          <label className="flex items-start gap-2 text-(--ld-text) text-sm">
            <input
              type="checkbox"
              checked={hideLeaves}
              onChange={(e) => setHideLeaves(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">{t("simplify.hideLeaves")}</span>
              <span className="block text-(--ld-muted) text-xs">
                {t("simplify.hideLeavesHint")}
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-(--ld-text) text-sm">
            <input
              type="checkbox"
              checked={cluster}
              onChange={(e) => setCluster(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">{t("simplify.cluster")}</span>
              <span className="block text-(--ld-muted) text-xs">{t("simplify.clusterHint")}</span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-(--ld-text) text-sm">
            <input
              type="checkbox"
              checked={critical}
              disabled={!canCriticalPath}
              onChange={(e) => setUseCriticalPath(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className={`font-medium ${canCriticalPath ? "" : "text-(--ld-muted)"}`}>
                {t("simplify.criticalPath")}
              </span>
              <span className="block text-(--ld-muted) text-xs">
                {canCriticalPath ? t("simplify.criticalPathReady") : t("simplify.criticalPathHint")}
              </span>
            </span>
          </label>

          {layersOnDiagram.length > 0 && (
            <div className="flex flex-col gap-1 border-(--ld-rule) border-t pt-2">
              <span className="font-medium text-(--ld-text) text-sm">
                {t("simplify.collapseLayer")}
              </span>
              <span className="text-(--ld-muted) text-xs">{t("simplify.collapseLayerHint")}</span>
              <div className="mt-1 flex flex-col gap-1">
                {layersOnDiagram.map((layer) => (
                  <label key={layer} className="flex items-center gap-2 text-(--ld-text) text-sm">
                    <input
                      type="checkbox"
                      checked={collapseLayers.has(layer)}
                      onChange={() => toggleLayer(layer)}
                    />
                    {t(`supernode.layers.${layer}`, { defaultValue: layer })}
                  </label>
                ))}
              </div>
            </div>
          )}

          {(promoteClusters.length > 0 || canPromoteSelection) && (
            <div className="flex flex-col gap-1 border-(--ld-rule) border-t pt-2">
              <span className="font-medium text-(--ld-text) text-sm">{t("simplify.promote")}</span>
              <span className="text-(--ld-muted) text-xs">{t("simplify.promoteHint")}</span>
              <div className="mt-1 flex flex-col gap-1">
                {promoteClusters.map((c) => (
                  <label
                    key={c.anchorId}
                    className="flex items-center gap-2 text-(--ld-text) text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={promoteAnchors.has(c.anchorId)}
                      onChange={() => togglePromote(c.anchorId)}
                    />
                    <span className="min-w-0 truncate">
                      {c.name || t("supernode.defaultLabel")}{" "}
                      <span className="text-(--ld-muted)">({c.memberIds.length})</span>
                    </span>
                  </label>
                ))}
                {canPromoteSelection && (
                  <label className="flex items-center gap-2 text-(--ld-text) text-sm">
                    <input
                      type="checkbox"
                      checked={promoteSelection}
                      onChange={(e) => setPromoteSelection(e.target.checked)}
                    />
                    {t("simplify.promoteSelection", { count: selectedConceptIds.length })}
                  </label>
                )}
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 border-(--ld-rule) border-t pt-2 text-(--ld-text) text-sm">
            <input
              type="checkbox"
              checked={autoLayout}
              onChange={(e) => setAutoLayout(e.target.checked)}
            />
            {t("simplify.autoLayout")}
          </label>

          <div className="flex items-center justify-between gap-2 pt-1">
            <span
              className={`min-w-0 flex-1 truncate text-xs ${
                msg?.kind === "error" ? "text-(--ld-negative)" : "text-(--ld-muted)"
              }`}
            >
              {msg?.text ?? ""}
            </span>
            <button
              type="button"
              onClick={generate}
              disabled={busy || !anyTransform}
              className="shrink-0 rounded bg-(--ld-primary) px-3 py-1.5 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:opacity-40"
            >
              {busy ? t("simplify.busy") : t("simplify.generate")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
